import { lazy } from 'react';
import { captureWarning } from './sentry';

// Recognised "I couldn't fetch the JS chunk for this dynamic import()" errors,
// across browsers and bundlers. Deliberately does NOT include Safari's bare
// "Load failed" — that surfaces for any fetch/RPC failure and would cause us
// to reload on transient network blips.
const CHUNK_ERROR_PATTERNS = [
  /Importing a module script failed/i,             // Safari
  /Failed to fetch dynamically imported module/i,  // Chrome/Edge
  /error loading dynamically imported module/i,    // Firefox
  /^Loading chunk \S+ failed/i,                    // Webpack/CRA legacy
];

const RELOAD_FLAG_KEY = 'chunk-reload-attempt';
const RELOAD_FLAG_TTL_MS = 60 * 1000;

function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = typeof err.message === 'string' ? err.message : '';
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(msg));
}

function shouldAttemptReload() {
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (!raw) return true;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return true;
    return Date.now() - ts > RELOAD_FLAG_TTL_MS;
  } catch {
    return true;
  }
}

function markReloadAttempted() {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
  } catch {
    /* sessionStorage unavailable — re-throw and let ErrorBoundary handle it */
  }
}

function clearReloadFlag() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
  } catch {
    /* ignore */
  }
}

// Wraps a dynamic import() so an atomic-deploy-invalidated chunk hash triggers
// a one-shot full reload (fetching the new HTML + new chunk graph) instead of
// dumping the user into the ErrorBoundary. Cleared on the first successful
// load so a future genuine chunk failure can reload again.
export function lazyWithReload(factory) {
  return lazy(() =>
    factory()
      .then((mod) => {
        clearReloadFlag();
        return mod;
      })
      .catch((err) => {
        if (isChunkLoadError(err) && shouldAttemptReload()) {
          // Leave a breadcrumb/event before the page tears down so this
          // forced reload is attributable (PII-free). reloadTelemetry.js also
          // records the post-reload landing via the chunk-reload-attempt flag,
          // which covers the case where this event can't flush before reload.
          captureWarning('Chunk load failed — forcing one-shot reload', {
            errorName: err?.name,
            errorMessage: typeof err?.message === 'string' ? err.message.slice(0, 200) : undefined,
            path: typeof window !== 'undefined' ? window.location.href.split('?')[0] : undefined,
          }, { flow: 'reload', stage: 'chunk-reload' });
          markReloadAttempted();
          window.location.reload();
          // Keep React in Suspense fallback while the reload is in flight,
          // instead of flashing the ErrorBoundary in the half-second before
          // the page tears down.
          return new Promise(() => {});
        }
        throw err;
      }),
  );
}
